import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface JoinGroupDialogProps {
  open: boolean
  /**
   * The group's display name, when an earlier lookup happened to turn one up
   * without joining first. Usually null: the server only hands out a group's
   * details to its members, which is the same check that put this dialog on
   * screen in the first place, so most of the time there is nothing to show
   * but the generic title.
   */
  groupName: string | null
  onJoin: () => void
  onCancel: () => void
}

/**
 * The one screen between a clicked invite link and the group it points at,
 * for someone who isn't a member yet. Mirrors the exit/clear confirmations on
 * a chat-list row: same Dialog, Cancel plus a single primary action, nothing
 * happens until Join is actually pressed.
 */
export function JoinGroupDialog({ open, groupName, onJoin, onCancel }: JoinGroupDialogProps) {
  const t = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {groupName
              ? t("presence.joinGroupTitleWithName", { name: groupName })
              : t("presence.joinGroupTitle")}
          </DialogTitle>
          <DialogDescription>{t("presence.joinGroupDesc")}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button onClick={onJoin}>
            {t("common.join")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
